/* React+Webpack Implementation of Reports - Drilldowns */
/* Since drilldown logic is substantial, we'll place it in its own module */

/* global AmCharts, reportModule */
/* eslint-disable dot-notation */
/* eslint-disable one-var */
/* eslint-disable one-var-declaration-per-line */

// commercial amcharts - different fron npm version
import '@dsisolutions/amcharts/amcharts';
import '@dsisolutions/amcharts/pie';
import '@dsisolutions/amcharts/radar';
import '@dsisolutions/amcharts/serial';
import '@dsisolutions/amcharts/themes/material';
import _ from 'lodash';
import moment from 'moment';
import RModal from 'rmodal/index';
import wordwrap from 'word-wrap';
import store from '../../store';
import toastr from '../toastr';
import Api from '../../services/ApiService';
import AuthService from '../../services/AuthService';
import '../../styles/animate-dsi.css';
import '../../styles/vendor/pikaday/pikaday.css';
import { modal } from '../modal/ModalEmitter';
import Pikaday from './vendor/pikaday/pikaday-dsi'; // DSI patched to resolve focus bug

// webpack AmCharts images, then set pathToImages on AmCharts object as /img/amcharts/
require.context('!!file-loader?name=img/amcharts/[name].[ext]!@dsisolutions/amcharts/images');

// Module state - see init() for default values and docs, reset on each mount
let lookupKey,
  agGridParams,
  branchID,
  chartObjs,
  gridRefresh,
  parentModalScrollTop,
  pikaDayObjs,
  reportConfig,
  showingModal,
  showingSubModal;

// DOM objects not available until the report react component mounts - see init()
let drilldownModalDiv, drilldownSubModalDiv, drilldownModalContent, drilldownSubModalContent;

let drilldownModal, drilldownSubModal;

// added when component mounts, removed when unmounts
// shared between init() and destroy(), so global to this module
function eventListenerModalEscape(event) {
  if (event.key === 'Escape' && (showingModal || showingSubModal)) {
    close(event);
  }
}

// initialize module before use (via ReportController::init when component mounts)
function init() {
  // 2 levels of modals (modal 1 can pop modal 2)
  drilldownModalDiv = document.getElementById('drilldownModalDiv');
  drilldownSubModalDiv = document.getElementById('drilldownSubModalDiv');
  drilldownModalContent = document.getElementById('drilldownModalContent');
  drilldownSubModalContent = document.getElementById('drilldownSubModalContent');

  agGridParams = null;
  branchID = undefined;
  chartObjs = [];
  lookupKey = ''; // set by getContextMenuItems params on right click
  parentModalScrollTop = 0;
  pikaDayObjs = [];
  showingModal = false;
  showingSubModal = false;

  // if a user clicks outside the modal, close it (not a feature of rmodal, use DOM hooks)
  const clickListener = function (evt) {
    /* For some reason, select controls on a modal trigger a click event with a location of (0,0) - weird DOM bug?
       For some other strange reason, any form with a button element will hit this with a location of (0,0) when
       a user presses enter on any input within that form (such as the first row of add collection note) claiming
       the button did it.  So weird!
    */
    if (_.includes(['BUTTON', 'OPTION', 'SELECT'], evt.target.tagName)) {
      return;
    }

    const modalContent = drilldownModalDiv.querySelector('.modal-content');
    // make sure we have modal content
    if (!modalContent) {
      return;
    }

    const modalRect = modalContent.getBoundingClientRect();
    if (
      evt.clientX < modalRect.left ||
      evt.clientX > modalRect.right ||
      evt.clientY > modalRect.bottom ||
      evt.clientY < modalRect.top
    ) {
      close(evt);
    }
  };

  // support the same feature for submodals, which have user defined dimensions
  const clickListenerSub = function (evt) {
    if (evt.target.tagName === 'SELECT' || evt.target.tagName === 'BUTTON') {
      return;
    }

    const modalContent = drilldownSubModalDiv.querySelector('.modal-content');
    // make sure we have modal content
    if (!modalContent) {
      return;
    }

    const modalRect = modalContent.getBoundingClientRect();
    if (
      evt.clientX < modalRect.left ||
      evt.clientX > modalRect.right ||
      evt.clientY > modalRect.bottom ||
      evt.clientY < modalRect.top
    ) {
      close(evt);
    }
  };

  drilldownModal = new RModal(drilldownModalDiv, {
    dialogOpenClass: 'animatedFast zoomIn',
    closeTimeout: 0,
    focus: true,
    escapeClose: false,
    afterOpen() {
      drilldownModalDiv.scrollTop = 0;
      document.addEventListener('click', clickListener);
      showingModal = true;
    },
    afterClose() {
      document.removeEventListener('click', clickListener);
      // cleanup any chart resources
      chartObjs.forEach(chartObj => {
        chartObj.clear(); // clear up amcharts resources
      });
      chartObjs = [];

      // cleanup pikaday resources (collections)
      pikaDayObjs.forEach(pikaDayObj => {
        pikaDayObj.destroy();
      });
      pikaDayObjs = [];

      showingModal = false;
    },
  });

  drilldownSubModal = new RModal(drilldownSubModalDiv, {
    dialogOpenClass: 'animatedFast zoomIn',
    closeTimeout: 0,
    focus: true,
    escapeClose: false,
    afterOpen() {
      parentModalScrollTop = drilldownModalDiv.scrollTop;
      drilldownSubModalDiv.scrollTop = 0;
      document.removeEventListener('click', clickListener); // change where we look for outside-modal clicks
      document.addEventListener('click', clickListenerSub);
      showingSubModal = true;
    },
    afterClose() {
      document.removeEventListener('click', clickListenerSub);
      drilldownModal.open(); // necessary to return scrollability to parent modal
      drilldownModalDiv.scrollTop = parentModalScrollTop; // restore scroll position
      // cleanup any chart resources
      chartObjs.forEach(chartObj => {
        chartObj.clear(); // clear up amcharts resources
      });
      chartObjs = [];
      showingSubModal = false;
    },
  });

  // adds ESCability to modal / submodal
  document.addEventListener('keydown', eventListenerModalEscape);
}

// called from ReportController when the react component unmounts
function destroy() {
  // close / destroy any currently open modals if user presses back button
  if (showingSubModal) {
    drilldownSubModal.close();
  }

  if (showingModal) {
    drilldownModal.close();
  }

  // cleanup rModal resources
  drilldownModal = null;
  drilldownSubModal = null;

  document.removeEventListener('keydown', eventListenerModalEscape);
}

// individual modal definitions (set width, embedded amcharts, etc.)
const modals = {};

modals['Accounting Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Accounting Detail',
  action() {
    modal.open('accountingDetail', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Add/View RO Notes'] = {
  icon: '<i class="fas fa-folder-open" />',
  name: 'Add/View Notes',
  action() {
    modal.open('repairNotes', {
      branchId: branchID,
      lookupKey,
      meta: {
        rowData: _.get(agGridParams, 'node.data'),
      },
    });
  },
};

modals['Add/View Technician Story'] = {
  icon: '<i class="fas fa-pencil-alt"></i>',
  name: 'Add/View Technician Story',
  action() {
    fetchDrilldown('addViewTechnicianStory').then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '750px';
      drilldownModal.open();
    });
  },
};

// requires a cashAcct column
modals['Check Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Check Detail',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.cashAcct}:${lookupKey}`;
    fetchDrilldown('checkDetail', lookupKeyOverride).then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '800px';
      drilldownModal.open();
    });
  },
};

// requires a dvs column
modals['Classic Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Classic Detail',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.dvs}:${lookupKey}`;
    fetchDrilldown('classicDetail', lookupKeyOverride).then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '850px';
      drilldownModal.open();
    });
  },
};

modals['Condition Information'] = {
  icon: '<i class="fas fa-info-circle"></i>',
  name: 'Condition Information',
  action() {
    modal.open('conditionInformation', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Contract Detail'] = {
  icon: '<i class="fas fa-file-contract"></i>',
  name: 'Contract Detail',
  action() {
    modal.open('contractDetail', {
      lookupKey,
    });
  },
};

modals['Customer A/R Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Customer A/R Detail',
  action() {
    // The same report may hit different lookup keys on the same row,
    // such as ACU3 Customer Name vs. Affiliate Name.  Thus, we must find
    // the affiliate name first to properly title the resultant modal.
    // Similarly with Customer A/R Summary and Paid Items.
    fetchDrilldown('customerName').then(name => {
      modal.open('customerArDetail', {
        branchId: branchID,
        lookupKey,
        title: `Customer AR Detail: ${name}`,
      });
    });
  },
};

modals['Customer A/R Summary'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Customer A/R Summary',
  action() {
    fetchDrilldown('customerName').then(name => {
      modal.open('customerArSummary', {
        branchId: branchID,
        lookupKey,
        title: `Customer AR Summary: ${name}`,
      });
    });
  },
};

modals['Customer Collections'] = {
  icon: '<i class="fas fa-folder-open" />',
  name: 'Customer Collections',
  action() {
    modal.open('customerCollections', {
      lookupKey,
    });
  },
};

modals['Customer Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Customer Detail',
  action() {
    modal.open('customerDetail', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Customer A/R History'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Customer A/R History',
  action() {
    modal.open('customerHistoryAR', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Customer History'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Customer History',
  action() {
    modal.open('customerHistory', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Customer Paid Items'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Customer Paid Items',
  action() {
    fetchDrilldown('customerName').then(name => {
      modal.open('customerDetailPaidItems', {
        branchId: branchID,
        lookupKey,
        title: `Customer Paid Items: ${name}`,
      });
    });
  },
};

// Customer detail paired with RO (for cash customers) (DBPARTCUST)
modals['Customer Repair Order Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Customer Detail',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.customerNo}!!:!!${agGridParams.node.data.roNo}`;
    modal.open('customerRepairOrderDetail', {
      branchId: branchID,
      lookupKey: lookupKeyOverride,
      title: `Customer: ${agGridParams.node.data.customerNo}`,
    });
  },
};

// includes amchart - dataProvider JSON encoded within detail.njk
// requires a dvs column
modals['Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Detail',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.dvs}:${lookupKey}`;
    modal.open('partDetail', {
      branchId: branchID,
      lookupKey: lookupKeyOverride,
      title: 'Part Detail',
    });
  },
};

modals['GL Detail'] = {
  icon: '<i class="fas fa-cash-register"></i>',
  name: 'GL Detail',
  action() {
    modal.open('glDetail', {
      lookupKey,
      title: 'Account Detail',
    });
  },
};

// requires a dvs column
modals['History'] = {
  icon: '<i class="fas fa-history"></i>',
  name: 'History',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.dvs}:${lookupKey}`;
    modal.open('partHistory', {
      branchId: branchID,
      lookupKey: lookupKeyOverride,
      title: 'Part History',
    });
  },
};

// requires a dvs column
modals['History By Customer'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'History By Customer',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.dvs}:${lookupKey}!!:!!${agGridParams.node.data.cusno}`;
    modal.open('partHistoryByCustomer', {
      branchId: branchID,
      lookupKey: lookupKeyOverride,
      title: 'Part History For Customer',
    });
  },
};

modals['Journals'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Journals',
  action() {
    const month = agGridParams.node.data.mnth;
    const source = agGridParams.node.data.src;
    const account = agGridParams.node.data.generalledger;
    const lookupKeyOverride = `${month}!!:!!${source}!!:!!${account}`;

    fetchDrilldown('journals', lookupKeyOverride).then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '1275px';
      drilldownModal.open();
    });
  },
};

modals['Open Backorders'] = {
  icon: '<i class="fas fa-folder-open"></i>',
  name: 'Open Backorders',
  action() {
    modal.open('openBackorders', {
      lookupKey,
      title: 'Open Backorders',
    });
  },
};

modals['Paccar Invoice'] = {
  icon: '<i class="fas fa-info"></i>',
  name: 'Paccar Invoice',
  action() {
    modal.open('paccarInvoice', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Confirmation Details'] = {
  icon: '<i class="fas fa-info"></i>',
  name: 'Confirmation Details',
  action() {
    modal.open('confirmationDetails', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['ConfirmationPIN1'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Edit Confirmation',
  action() {
    window.open(`/PIN1/${agGridParams.node.data.cusno}/${lookupKey}`, '_blank');
  },
};

modals['ConfirmationPRO1'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Edit Confirmation',
  action() {
    window.open(`/PRO1/${lookupKey}/${agGridParams.node.data.confirmation}`, '_blank');
  },
};

modals['Part History'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Parts On Repair Order',
  action() {
    modal.open('repairOrderPartHistory', {
      branchId: branchID,
      lookupKey,
    });
  },
};

// requires a dvs column
modals['Part Locations'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Part Locations',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.dvs}:${lookupKey}`;
    modal.open('partLocations', {
      branchId: branchID,
      lookupKey: lookupKeyOverride,
      title: 'Part Locations',
    });
  },
};

// See STP10 for candidate ROs
modals['Part Requests'] = {
  icon: '<i class="fas fa-inbox"></i>',
  name: 'Part Requests',
  action() {
    modal.open('technicianPartsRequests', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Price Code Information'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Price Code Information',
  action() {
    modal.open('priceCodeInformation', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Price Check'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Price Check',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.partNum}!!:!!${agGridParams.node.data.dvs}!!:!!${agGridParams.node.data.customerNo}`;
    fetchDrilldown('priceCheck', lookupKeyOverride).then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '850px';
      drilldownModal.open();
    });
  },
};

modals['Purchase Order Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Purchase Order Detail',
  action() {
    modal.open('purchaseOrderDetail', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Purchase Order Summary'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Purchase Order Summary',
  action() {
    modal.open('purchaseOrderSummary', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Credit Hold Attachments'] = {
  icon: '<i class="fas fa-paperclip"></i>',
  name: 'Credit Hold Attachments',
  action() {
    modal.open('attachments', {
      itemId: lookupKey,
      itemName: 'AAR2',
    });
  },
};

modals['RO Attachments'] = {
  icon: '<i class="fas fa-paperclip"></i>',
  name: 'RO Attachments',
  action() {
    modal.open('attachments', {
      itemId: lookupKey,
      itemName: 'SMM1',
    });
  },
};

modals['Time Clock'] = {
  icon: '<i class="fas fa-clock"></i>',
  name: 'Time Clock',
  action() {
    // browserHistory.push('/STP2');
    modal.open('timeclock', { clockType: 'mechanics' });
  },
};

modals['Edit Stockclass'] = {
  icon: '<i class="fas fa-edit"></i>',
  name: 'Edit Stockclass',
  action() {
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'PSC1',
      lookupKey,
      render: 'listEditItem',
      title: `Edit Stockclass ${lookupKey}`,
    });
  },
};

modals['Edit Accounting Customer'] = {
  icon: '<i class="fas fa-edit"></i>',
  name: 'Edit Customer',
  action() {
    const lookupKeyOverride = agGridParams.node.data.customerNo || agGridParams.node.data.cusno;
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'ACU1',
      lookupKey: lookupKeyOverride,
      render: 'listEditItem',
      title: `Edit Accounting Customer ${lookupKey}`,
    });
  },
};

modals['Edit Customer'] = {
  icon: '<i class="fas fa-edit"></i>',
  name: 'Edit Customer',
  action() {
    const lookupKeyOverride = agGridParams.node.data.customerNo || agGridParams.node.data.cusno;
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'PCU2',
      lookupKey: lookupKeyOverride,
      render: 'listEditItem',
      title: `Edit Customer ${lookupKey}`,
    });
  },
};

modals['Create New RO'] = {
  icon: '<i class="fas fa-plus-circle"></i>',
  name: 'Create New RO',
  action() {
    modal.open('listEditItem', {
      action: 'new',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'SMM1',
      lookupKey: 'New',
      render: 'listEditItem',
      title: 'Create New RO',
    });
  },
};

modals['Edit RO'] = {
  icon: '<i class="fas fa-edit"></i>',
  name: 'Edit RO',
  action() {
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'SMM1',
      lookupKey,
      render: 'listEditItem',
      title: `Edit Repair Order ${lookupKey}`,
    });
  },
};

modals['Jobsite Information'] = {
  icon: '<i class="fad fa-user-hard-hat"</i>',
  name: 'Jobsite Information',
  action() {
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'SMM1A',
      lookupKey,
      render: 'listEditItem',
      title: `Jobsite Information ${lookupKey}`,
    });
  },
};

modals['SS Edit Vehicle'] = {
  icon: '<i class="fas fa-truck"></i>',
  name: 'Edit Vehicle',
  action() {
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'SUF2',
      lookupKey: agGridParams.node.data.vinNo,
      render: 'listEditItem',
      title: `Vehicle ${agGridParams.node.data.vinNo}`,
    });
  },
};

modals['Close RO'] = {
  icon: '<i class="fas fa-window-close"></i>',
  name: 'Close RO',
  action() {
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'SMM6',
      lookupKey,
      render: 'listEditItem',
      title: `Close Repair Order ${lookupKey}`,
    });
  },
};

modals['Service Tag'] = {
  icon: '<i class="fas fa-print"></i>',
  name: 'Service Tag',
  action() {
    modal.open('editItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'SMB2',
      lookupKey,
      query: { repairOrder__2: lookupKey },
      render: 'editItem',
      title: `Print Service Tag For ${lookupKey}`,
    });
  },
};

modals['Edit Part'] = {
  icon: '<i class="fas fa-edit"></i>',
  name: 'Edit Part',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.dvs}:${lookupKey}`;
    const branchId = _.get(agGridParams, 'node.data.bzBranchID', undefined);

    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'PIV1',
      lookupKey: lookupKeyOverride,
      render: 'listEditItem',
      branchId,
      title: `Edit Part ${lookupKeyOverride}`,
    });
  },
};

modals['Rental Billing Adjustment'] = {
  icon: '<i class="fas fa-edit"></i>',
  name: 'Rental Billing Adjustment',
  action() {
    modal.open('listEditItem', {
      action: 'edit',
      dialogClass: 'fitted',
      headerClass: 'primary',
      hideFooter: true,
      hidePrint: true,
      itemName: 'LRB8',
      lookupKey,
      render: 'listEditItem',
      title: `Rental Billing Adjustment - ${lookupKey}`,
    });
  },
};

modals['RO Attachments Read Only'] = {
  icon: '<i class="fas fa-paperclip"></i>',
  name: 'RO Attachments', // no need for "Read Only" within interface
  action() {
    modal.open('attachments', {
      itemId: lookupKey,
      itemName: 'SMM1',
      readOnly: true,
    });
  },
};

modals['Schedule Notes'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Schedule Notes',
  action() {
    const schedule = agGridParams.node.data.Schedule.split(' ')[0]; // strip out just the schedule no
    const lookupKeyOverride = `${schedule}!!:!!${lookupKey}`;

    fetchDrilldown('scheduleNotes', lookupKeyOverride).then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '850px';
      drilldownModal.open();
    });
  },
};

// shop status vehicle history, slightly different from other Vehicle History
modals['SS Vehicle History'] = {
  icon: '<i class="fas fa-history"></i>',
  name: 'Vehicle History',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.vinNo}`;
    modal.open('vehicleHistory', {
      lookupKey: lookupKeyOverride,
      title: 'Vehicle History',
      branchId: branchID,
    });
  },
};

// shop status vehicle information
modals['SS Vehicle Information'] = {
  icon: '<i class="fas fa-truck-container"></i>',
  name: 'Vehicle Information',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.vinNo}`;
    modal.open('vehicleInformation', {
      lookupKey: lookupKeyOverride,
      title: 'Vehicle Detail',
      branchId: branchID,
    });
  },
};

modals['SS Vehicle Part History'] = {
  icon: '<i class="fas fa-tools"></i>',
  name: 'Vehicle Part History',
  action() {
    const lookupKeyOverride = `${agGridParams.node.data.vinNo}`;
    modal.open('vehiclePartHistory', {
      lookupKey: lookupKeyOverride,
      title: 'Vehicle Part History',
      branchId: branchID,
    });
  },
};

modals['Stockclass Detail'] = {
  icon: '<i class="fas fa-info-square"></i>',
  name: 'Stockclass Detail',
  action() {
    modal.open('stockclassDetail', {
      lookupKey,
      title: 'Stock Class Details',
      branchId: branchID,
    });
  },
};

modals['Stockclass Parts'] = {
  icon: '<i class="fas fa-hammer"></i>',
  name: 'Parts In Stockclass',
  action() {
    modal.open('partsInStockClass', {
      lookupKey,
      title: 'Parts In Stock Class',
      branchId: branchID,
    });
  },
};

modals['Stockclass Listing'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Stockclass Listing',
  action() {
    fetchDrilldown('stockclassListing').then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '700px';
      drilldownModal.open();
    });
  },
};

// Reads modal data from hidden columns and renders client side
modals['Technician Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Technician Detail',
  action() {
    // WEBRO5 -> Name -> Technician Detail
    const data = `<div>
      <div class="modal-content">
        <div class="modal-header">
          <div class="row">
            <div class="col-xs-8">
              <h4 class="modal-title" id="report-modal-header">Technican: ${agGridParams.node.data.mecnam}</h4>
            </div>
            <div class="col-xs-4" style="text-align:right">
              <i class="zmdi zmdi-print" onclick="window.print()"></i>
              <i class="zmdi zmdi-close" onclick="drilldownsModule.close(event)" style="margin: 8px 0 0 10px"></i>
            </div>
          </div>
        </div>
        <div class="modal-body">
          <table class="table table-hover" style="font-size:1.10em;" id="report-modal-data-table">
            <tbody>
              <tr>
                <td style="width:25%; font-weight:bold;">Group #</td>
                <td>${agGridParams.node.data.groupnum}</td>
              </tr>
              <tr>
                <td style="width:25%; font-weight:bold;">Hire Date</td>
                <td>${agGridParams.node.data.hiredate}</td>
              </tr>
              <tr>
                <td style="width:25%; font-weight:bold;">Start Time</td>
                <td>${agGridParams.node.data.startdate}</td>
              </tr>
              <tr>
                <td style="width:25%; font-weight:bold;">Certification</td>
                <td>${agGridParams.node.data.cert}</td>
              </tr>
              <tr>
                <td style="width:25%; font-weight:bold;">Regular Hours</td>
                <td>${agGridParams.node.data.reghours}</td>
              </tr>
          </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" onclick="drilldownsModule.close(event)">Close</button>
        </div>
      </div>
    </div>`;

    // timeout required for modal to display
    setTimeout(() => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '700px';
      drilldownModal.open();
    }, 0);
  },
};

modals['Technician Hours'] = {
  icon: '<i class="fas fa-hourglass-end"></i>',
  name: 'Technician Hours',
  action() {
    modal.open('technicianHours', {
      lookupKey,
      title: 'Technician Hours',
    });
  },
};

modals['Technician Notes'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Technician Notes',
  action() {
    fetchDrilldown('technicianNotes').then(data => {
      drilldownModalContent.innerHTML = data;
      drilldownModalContent.style.width = '950px';
      drilldownModal.open();
    });
  },
};

modals['Vehicle Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Vehicle Detail',
  action() {
    modal.open('vehicleInformation', {
      lookupKey,
      title: 'Vehicle Details',
    });
  },
};

modals['Vehicle History'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Vehicle History',
  action() {
    modal.open('vehicleHistory', {
      lookupKey,
      title: 'Vehicle History',
    });
  },
};

modals['Vehicle Part History'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Vehicle Part History',
  action() {
    modal.open('vehiclePartHistory', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Vehicle Specifications'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Vehicle Specifications',
  action() {
    modal.open('vehicleSpecSheet', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Vendor A/P Detail'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Vendor A/P Detail',
  action() {
    modal.open('vendorApDetail', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Vendor Information'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Vendor Information',
  action() {
    modal.open('vendorInformation', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['Vendor Information Parts'] = {
  icon: '<i class="fas fa-th-list"></i>',
  name: 'Vendor Information',
  action() {
    modal.open('vendorInformationParts', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['View and Print Invoice'] = {
  icon: '<i class="fas fa-print"></i>',
  name: 'View and Print Invoice',
  action() {
    modal.open('viewInvoice', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['View and Print Picklist/Quote'] = {
  icon: '<i class="fas fa-print"></i>',
  name: 'View and Print Picklist/Quote',
  action() {
    modal.open('pickListView', {
      branchId: branchID,
      lookupKey,
    });
  },
};

modals['View and Print Lease & Rental Invoice'] = {
  icon: '<i class="fas fa-print"></i>',
  name: 'View and Print Invoice',
  action() {
    modal.open('leaseRentalInvoice', {
      branchId: branchID,
      lookupKey,
    });
  },
};

// Used by Add/View Collection Notes Modal
function addCollectionNote() {
  document.getElementById('addCollectionDiv').style.display = 'block';
  document.getElementById('customerCollectionsToolbar').style.display = 'none';
  document.getElementById('collectionContact').focus();
}

// Only used by Shop Status - no multibranch concerns
function addRONote(event) {
  const note = document.getElementById('addRoNote').value;
  if (note === '') {
    return; // keep modal open if input empty
  }

  close(event); // close modal then submit, pop toastr on error

  const postBody = {
    roNo: lookupKey,
    note,
  };

  Api.post('report/addronote', postBody)
    .then(res => {
      if (res.data !== 200) {
        throw new Error();
      }
      agGridParams.node.setDataValue('lastRepairOrderNote', note); // update live grid
      toastr.success('Success!', `Note added to RO# ${lookupKey}.`);
    })
    .catch(() => {
      toastr.error('Error!', `Unable to add note to RO# ${lookupKey}.  Try again later.`);
    });
}

// no multibranch concerns (AMS7/AMS9/AMP5/SAM7 - not multibranch)
function addScheduleNote(event) {
  const note = document.getElementById('addScheduleNote').value;
  if (note === '') {
    return; // keep modal open if input empty
  }

  close(event); // close modal then submit, pop toastr on error

  const schedule = agGridParams.node.data.Schedule.split(' ')[0]; // strip out just the schedule no
  const control = lookupKey;

  const postBody = {
    schedule,
    control,
    note,
  };

  Api.post('report/addschedulenote', postBody)
    .then(res => {
      if (res.data !== 200) {
        throw new Error();
      }
      toastr.success('Success!', 'Schedule note added.');
    })
    .catch(() => {
      toastr.error('Error!', 'Unable to add schedule note.  Try again later.');
    });
}

// Used by Add/View Technician Story Modal
function addStory() {
  document.getElementById('addStoryDiv').style.display = 'block';
  document.getElementById('techStoryToolbar').style.display = 'none';
}

function cancelCollectionNote(event) {
  document.getElementById('collectionContact').value = '';
  document.getElementById('collectionContactDate').value = '';
  document.getElementById('collectionContactTime').value = '';
  document.getElementById('collectionPromiseAmount').value = '';
  document.getElementById('collectionPromiseDate').value = '';
  document.getElementById('collectionNote').value = '';
  document.getElementById('addCollectionDiv').style.display = 'none';
  document.getElementById('customerCollectionsToolbar').style.display = 'block';
  setCollectionTimeToNow(event);
}

function cancelStory() {
  document.getElementById('correctionInput').value = '';
  document.getElementById('causeInput').value = '';
  document.getElementById('technicianSelect').value = 'select';
  document.getElementById('operationSelect').value = 'select';
  document.getElementById('addStoryDiv').style.display = 'none';
  document.getElementById('techStoryToolbar').style.display = 'block';
}

function clearCollection() {
  const clearIDs = [
    'collectionContact',
    'collectionContactDate',
    'collectionContactTime',
    'collectionPromiseAmount',
    'collectionPromiseDate',
    'collectionNote',
  ];

  clearIDs.forEach(id => {
    const el = document.getElementById(id);
    el.value = '';
    el.style.border = ''; // clear any error backgrounds
  });

  document.getElementById('collectionContact').focus();
}

// Clears any form errors on change (until revalidated)
function clearCollectionError(id) {
  const formError = document.getElementById(id);
  formError.style.border = '';
}

function clearStory() {
  document.getElementById('technicianSelect').value = 'select';
  document.getElementById('operationSelect').value = 'select';
  document.getElementById('correctionInput').value = '';
  const causeInput = document.getElementById('causeInput');
  causeInput.value = '';
  causeInput.focus();
}

function close(event) {
  if (showingSubModal) {
    drilldownSubModal.close();
    event.stopPropagation();

    // inferred, but check anyway
  } else if (showingModal) {
    drilldownModal.close();
    event.stopPropagation();
  }
}

// Uses the current cell value as the lookup key unless explicitly overridden
function fetchDrilldown(drilldown, lookupKeyOverride = null, submodal = false) {
  if (lookupKeyOverride) {
    lookupKey = lookupKeyOverride;
  }

  return new Promise((resolve, reject) => {
    const postBody = {
      branchID,
      drilldown,
      lookupKey,
      submodal,
    };
    Api.post('report/drilldown', postBody)
      .then(res => {
        res.data = res.data.replace(/\$NaN/g, '');
        resolve(res.data);
      })
      .catch(() => {
        reject('fetch error');
      });
  });
}

// sets up the context menu for each ag-grid cell
function getContextMenuItems(params) {
  // prevent exception if right clicking on empty grid space
  if (params.column === null) {
    return null;
  }

  // disable context menus on group rows and aggregation rows, but allow copying aggregate values
  if (params.node.group || params.node.rowPinned) {
    return ['copy', 'copyWithHeaders'];
  }

  const rangecharts = _.get(reportConfig, 'config.page.Report.rangecharts', true);

  // suppress context menu on empty cells (allow WEBRO2 nextTech & location)
  if (params.value === '') {
    if (reportConfig.reportName.startsWith('WEBRO2')) {
      if (params.column.colId !== 'nextTech' && params.column.colId !== 'location' && params.column.colId !== 'status') {
        return rangecharts ? ['chartRange'] : null;
      }
    }
  }

  agGridParams = params; // lets individual modals access the passed in context params
  branchID = _.get(params, 'node.data.bzBranchID', undefined); // undefined on non-mb reports
  lookupKey = params.value; // lets individual modals access the lookupKey

  let contextMenu = [];

  // setup modal groups here - each case matches YAML helpers definition possibilities
  switch (params.column.colDef.helpers) {
    case 'accountingCustomer':
      contextMenu.push(
        modals['Customer Detail'],
        modals['Customer A/R Detail'],
        modals['Customer A/R Summary'],
        modals['Customer Paid Items'],
        modals['Customer Collections'],
        modals['Customer A/R History'],
      );
      if (AuthService.hasPermission('ACU1')) {
        contextMenu.push('separator');
        contextMenu.push(modals['Edit Accounting Customer']);
      }
      break;
    case 'accountingCustomerEdit':
      if (AuthService.hasPermission('ACU1')) {
        contextMenu.push('separator');
        contextMenu.push(modals['Edit Accounting Customer']);
      }
      break;
    case 'accountingInvoiceDetail':
      contextMenu.push(
        modals['Accounting Detail'],
        modals['View and Print Invoice'],
      );
      break;
    case 'accountingVendor':
      contextMenu.push(
        modals['Vendor Information'],
        modals['Vendor A/P Detail']
      );
      break;
    case 'contractDetail':
      contextMenu.push(
        modals['Contract Detail'],
      );
      break;
    case 'check':
      contextMenu.push(
        modals['Check Detail'],
      );
      break;

    case 'creditHold':
      contextMenu.push(
        modals['Credit Hold Attachments'],
      );
      break;

    case 'customerRepairOrderDetail':
      contextMenu.push(
        modals['Customer Repair Order Detail']
      );

      if (AuthService.hasPermission('PCU2')) {
        contextMenu.push('separator');
        contextMenu.push(modals['Edit Customer']);
      }

      break;
    case 'glDetail':
      contextMenu.push(
        modals['GL Detail']
      );
      break;
    case 'journals':
      contextMenu.push(
        modals['Journals']
      );
      break;
    case 'leaseAndRentalInvoice':
      contextMenu.push(
        modals['View and Print Lease & Rental Invoice']
      );
      break;
    case 'mechDetail':
      contextMenu.push(
        modals['Technician Detail']
      );
      break;
    case 'openRepairOrderClass':
      contextMenu.push(
        modals['Part History'],
        modals['Condition Information'],
        modals['View and Print Invoice'],
        modals['Technician Hours'],
        modals['Technician Notes'],
      );
      if (AuthService.hasPermission('UCS1')) {
        contextMenu.push({
          icon: '<i class="fas fa-signature"></i>',
          name: 'Sign Repair Order',
          action() {
            window.open(`/UCS1/${lookupKey}`, '_blank');
          },
        });
      }
      break;
    case 'paccarInvoice':
      contextMenu.push(
        modals['Paccar Invoice']
      );
      break;
    case 'confirmationDetails':
      contextMenu.push(
        modals['Confirmation Details'],
        modals['ConfirmationPIN1'],
      );
      break;
    case 'confirmationROJump':
      contextMenu.push(
        modals['ConfirmationPRO1'],
        modals['Part History'],
        modals['Condition Information'],
      );
      break;
    case 'partCustomer':
      contextMenu.push(
        modals['Customer Detail'],
        modals['Customer History']
      );

      if (AuthService.hasPermission('PCU2')) {
        contextMenu.push('separator');
        contextMenu.push(modals['Edit Customer']);
      }

      break;
    case 'partDetail':
      contextMenu.push(
        modals['Part Detail']
      );
      break;
    case 'partInvoiceManagementSchedule':
      contextMenu.push(
        modals['Schedule Notes'],
        modals['View and Print Invoice'],
      );
      if (AuthService.hasPermission('UCS1')) {
        contextMenu.push({
          icon: '<i class="fas fa-signature"></i>',
          name: 'Sign Invoice',
          action() {
            window.open(`/UCS1/${lookupKey}`, '_blank');
          },
        });
      }
      break;
    case 'partInvoice':
      contextMenu.push(
        modals['View and Print Invoice'],
      );
      if (AuthService.hasPermission('UCS1')) {
        contextMenu.push({
          icon: '<i class="fas fa-signature"></i>',
          name: 'Sign Invoice',
          action() {
            window.open(`/UCS1/${lookupKey}`, '_blank');
          },
        });
      }
      break;
    case 'partNumber':
      contextMenu.push(
        modals['History'],
        modals['Detail'],
        modals['Part Locations'],
      );

      if (AuthService.hasPermission('PIV1', branchID)) {
        contextMenu.push(
          modals['Edit Part'],
        );
      }
      break;
    case 'partNumberHistByCust':
      contextMenu.push(
        modals['History'],
        modals['History By Customer'],
        modals['Detail'],
        modals['Part Locations'],
      );
      break;
    case 'partNumberWithPriceCheck':
      contextMenu.push(
        modals['History'],
        modals['Detail'],
        modals['Price Check']
      );
      break;
    case 'partStockClass':
      contextMenu.push(modals['Stockclass Detail']);
      contextMenu.push(modals['Stockclass Parts']);

      if (AuthService.hasPermission('PSC1')) {
        contextMenu.push('separator');
        contextMenu.push(modals['Edit Stockclass']);
      }
      break;
    case 'partsVendor':
      contextMenu.push(
        modals['Vendor Information Parts']
      );
      break;
    case 'picklist':
      contextMenu.push(
        modals['View and Print Picklist/Quote']
      );
      break;
    case 'priceCode':
      contextMenu.push(
        modals['Price Code Information']
      );
      break;
    case 'purchaseOrder':
      contextMenu.push(
        modals['Purchase Order Detail'],
        modals['Purchase Order Summary']
      );
      break;
    case 'rentalBillingAdjustment':
      if (AuthService.hasPermission('LRB8')) {
        contextMenu.push('separator');
        contextMenu.push(modals['Rental Billing Adjustment']);
      }
      break;
    case 'repairOrderClass':
      contextMenu.push(
        modals['Part History'],
        modals['Condition Information'],
        modals['Technician Hours'],
        modals['View and Print Invoice'],
        modals['Technician Notes'],
        modals['RO Attachments Read Only'],
      );
      if (AuthService.hasPermission('UCS1')) {
        contextMenu.push({
          icon: '<i class="fas fa-signature"></i>',
          name: 'Sign Repair Order',
          action() {
            window.open(`/UCS1/${lookupKey}`, '_blank');
          },
        });
      }
      break;
    case 'repairOrderPartsClass':
      contextMenu.push(
        modals['Part History'],
        modals['Condition Information'],
        modals['Technician Hours'],
        modals['RO Attachments Read Only'],
      );
      break;
    case 'repairOrderWebSolutions':
      contextMenu.push(
        modals['Part History'],
        modals['Part Requests'],
        modals['Open Backorders'],
        modals['Condition Information'],
        modals['Technician Hours'],
        modals['View and Print Invoice'],
      );

      if (AuthService.hasPermission('UCS1')) {
        contextMenu.push({
          icon: '<i class="fas fa-signature"></i>',
          name: 'Sign Hard Card',
          action() {
            window.open(`/UCS1/${lookupKey}`, '_blank');
          },
        });
      }

      if (AuthService.hasPermissionToOne(['SMM1', 'SMM6', 'PRO1', 'SMM3', 'SMB2'])) {
        contextMenu.push('separator');

        if (AuthService.hasPermission('SMM1')) {
          contextMenu.push(modals['Create New RO']);
          contextMenu.push(modals['Edit RO']);
          contextMenu.push(modals['Jobsite Information']);
        }

        if (AuthService.hasPermission('SMM3')) {
          contextMenu.push({
            icon: '<i class="fas fa-edit"></i>',
            name: 'Issue Sublets',
            action() {
              window.open(`/SMM3/${lookupKey}`, '_blank');
            },
          });
        }

        if (AuthService.hasPermission('PRO1')) {
          contextMenu.push({
            icon: '<i class="fas fa-edit"></i>',
            name: 'Issue Parts',
            action() {
              window.open(`/PRO1/${lookupKey}`, '_blank');
            },
          });
        }

        if (AuthService.hasPermission('SMM6')) {
          contextMenu.push(modals['Close RO']);
        }

        if (AuthService.hasPermission('SMB2')) {
          contextMenu.push(modals['Service Tag']);
        }
      }

      if (AuthService.hasPermission('STP2')) {
        contextMenu.push('separator');
        if (AuthService.hasPermission('STP2')) {
          contextMenu.push(modals['Time Clock']);
        }
      }

      contextMenu.push(
        'separator',
        modals['RO Attachments'],
        'separator',
        modals['Add/View RO Notes'],
        modals['Add/View Technician Story'],
      );
      break;
    case 'scheduleNotes':
      contextMenu.push(
        modals['Schedule Notes']
      );
      break;

    case 'shopStatusVehicleDetail':
      contextMenu.push(
        { name: `VIN: ${agGridParams.node.data.vinNo}` },
        modals['SS Vehicle History'], // slightly different from Vehicle History below
        modals['SS Vehicle Information'],
        modals['SS Vehicle Part History']
      );
      if (AuthService.hasPermission('SUF2')) {
        contextMenu.push('separator');
        contextMenu.push(modals['SS Edit Vehicle']);
      }
      break;

    case 'signatureCapture':
      if (AuthService.hasPermission('UCS1')) {
        contextMenu.push({
          icon: '<i class="fas fa-signature"></i>',
          name: 'Sign Document',
          action() {
            window.open(`/UCS1/${lookupKey}`, '_blank');
          },
        });
      }
      break;

    case 'vehicleDetailClass':
      contextMenu.push(
        modals['Vehicle History'],
        modals['Vehicle Part History'],
        modals['Open Purchase Orders'],
        modals['Closed Purchase Orders'],
        modals['Vehicle Detail']
      );
      break;
    case 'vehicleSpec':
      contextMenu.push(
        modals['Vehicle Specifications']
      );
      break;
    case 'WEBPI4': // custom dynamic modal based on column name
      lookupKey = params.column.colDef.field; // lookupKey here is dynamic col name, not cell value
      contextMenu.push(
        modals['Stockclass Listing']
      );
      break;
    default:
      // no helpers
      break;
  }

  // custom context menu for shop status assignment (code-fold this block if not considering shop status)
  if (reportConfig.reportName.startsWith('WEBRO2') && params.column.colId === 'status') {
    function contextMenuPush(code) { // eslint-disable-line
      contextMenu.push({
        name: code.desc,
        icon: code.icon,
        action() {
          // only run on changes
          if (code.code === params.value) {
            return;
          }

          params.node.setDataValue('status', code.code);
          const postBody = {
            roNo: params.node.data.roNo,
            code: code.code,
            ppc: reportModule.getPPC(),
          };
          const previousValue = params.value; // revert UI if update fails

          Api.post('report/shopstatusupdate', postBody)
            .then(res => {
              if (res.data !== 200) {
                throw new Error();
              }
              gridRefresh(); // update widgets real time
            })
            .catch(() => {
              params.node.setDataValue('status', previousValue);
              toastr.error('Error!', `Unable to update status for RO# ${params.node.data.roNo}.  Try again later.`);
            });
        },
      });
    }

    // Outlier unassigned case not included in statusCodes from D3/QM, no icon either
    contextMenuPush({
      code: '',
      desc: 'Unassigned',
      icon: null,
    });

    // All other codes are dynamically provided by D3/QM and can change externally between shop status runs
    const { codes, user } = store.getState().app;

    const codesForBranch = codes.filter(c =>
      c.branches.includes(_.get(user, 'branch.id'))
    );

    codesForBranch.forEach(code => {
      if (code.statusTypeId !== 1) {
        return;
      }

      contextMenuPush({
        code: code.id,
        desc: code.name,
        icon: `<i class="fas fa-stop" style="color:${code.color}"></i>`,
      });
    });
  }

  if (reportConfig.reportName.startsWith('WEBRO2') && params.column.colId === 'location') {
    const { codes } = store.getState().app;

    codes.forEach(location => {
      if (location.statusTypeId !== 3) {
        return;
      }

      contextMenu.push({
        name: location.name,
        icon: `<i class="fas fa-stop" style="color:${location.color}"></i>`,
        action() {
          params.node.setDataValue('location', location.id);
        },
      });
    });
  }

  // custom context menu for shop status assignment (code-fold this block if not considering shop status)
  if (reportConfig.reportName.startsWith('WEBRO2') && params.column.colId === 'nextTech') {
    function contextMenuPush(activeTech) { // eslint-disable-line
      contextMenu.push({
        name: activeTech.techName,
        icon: activeTech.icon,
        action() {
          // only run on changes
          if (activeTech.techNo === params.value) {
            return;
          }

          params.node.setDataValue('nextTech', activeTech.techNo);
          const postBody = {
            roNo: params.node.data.roNo,
            techNo: activeTech.techNo,
          };
          const previousValue = params.value; // revert UI if update fails

          Api.post('report/shopstatusnexttechupdate', postBody)
            .then(res => {
              if (res.data !== 200) {
                throw new Error();
              }
              gridRefresh(); // update widgets real time
            })
            .catch(() => {
              params.node.setDataValue('nextTech', previousValue);
              toastr.error('Error!', `Unable to update next tech for RO# ${params.node.data.roNo}.  Try again later.`);
            });
        },
      });
    }

    // Outlier unassigned case not included in activeTechs from D3/QM, no icon either
    contextMenuPush({
      techNo: '',
      techName: 'Unassigned',
      icon: null,
    });

    // All other codes are dynamically provided by D3/QM and can change externally between shop status runs
    reportConfig.activeTechs.forEach(activeTech => {
      contextMenuPush({
        techNo: activeTech.techNo,
        techName: `${activeTech.techNo}: ${activeTech.techName}`,
        icon: '<i class="fas fa-user" style="font-size:12px"></i>',
      });
    });
  }

  // custom context menu for WEBRO3 part status
  if (reportConfig.reportName === 'WEBRO3' && params.column.colId === 'status') {
    function contextMenuPush(code) { // eslint-disable-line
      contextMenu.push({
        name: code.desc,
        icon: code.icon,
        action() {
          // only run on changes
          if (code.desc === params.value) {
            return;
          }

          params.node.setDataValue('status', code.id);

          const postBody = {
            key: params.node.data.key,
            statusId: code.id,
          };
          const previousValue = params.value; // revert UI if update fails

          Api.post('report/partrequeststatusupdate', postBody)
            .then(res => {
              if (res.data !== 200) {
                throw new Error();
              }
              gridRefresh(); // update widgets real time
            })
            .catch(() => {
              params.node.setDataValue('status', previousValue);
              toastr.error('Error!', `Unable to update status for part request ${params.node.data.key}.  Try again later.`);
            });
        },
      });
    }

    // All other codes are dynamically provided by D3/QM and can change externally between part request runs
    const { codes } = store.getState().app;

    codes.forEach(code => {
      if (code.statusTypeId !== 2) {
        return;
      }

      contextMenuPush({
        id: code.id,
        desc: code.name,
        icon: `<i class="fas fa-stop" style="color:${code.color}"></i>`,
      });
    });
  }

  // custom context menu for PMR4/SSR9 dynamic month-year totals columns
  if (_.includes(['PMR4', 'SSR9'], reportConfig.reportName) && /^\d{1,2}-\d{4}$/.test(params.column.colId)) {
    contextMenu.push({
      icon: '<i class="fas fa-th-list"></i>',
      name: 'Invoice History',
      action() {
        const opts = reportModule.getReportOptions();
        const beginningDate = _.get(_.find(opts, o => o.label === 'Beginning Date'), 'value', '');
        const endingDate = _.get(_.find(opts, o => o.label === 'Ending Date'), 'value', '');
        const includeLabor = _.get(_.find(opts, o => o.label === 'Include Labor'), 'value', 'Y');
        const reportOnCounter = _.get(_.find(opts, o => o.label === 'Report On (Counter)'), 'value', 'P');
        const reportOnSalesman = _.get(_.find(opts, o => o.label === 'Report On (Salesman)'), 'value', 'P');
        const reportOnInternal = _.get(_.find(opts, o => o.label === 'Include Internal'), 'value', 'N');
        const reportOnWarranty = _.get(_.find(opts, o => o.label === 'Include Warranty'), 'value', 'N');
        const reportMode = 'month';

        const lookupKeyOverride = [
          params.column.colId,
          params.node.data.cusno,
          includeLabor,
          reportOnCounter,
          reportOnSalesman,
          beginningDate,
          endingDate,
          reportOnInternal,
          reportOnWarranty,
          reportMode,
        ].join('!!:!!');

        fetchDrilldown('invoiceHistory', lookupKeyOverride).then(data => {
          drilldownModalContent.innerHTML = data;
          drilldownModalContent.style.width = '650px';
          drilldownModal.open();
        });
      },
    });
  }

  // custom context menu for PMR4/SSR9 dynamic month-year totals columns
  if (_.includes(['PMR4A'], reportConfig.reportName) && /^\d{1,2}-\d{1,2}-\d{4}$/.test(params.column.colId)) {
    contextMenu.push({
      icon: '<i class="fas fa-th-list"></i>',
      name: 'Invoice Historyy',
      action() {
        const opts = reportModule.getReportOptions();
        const beginningDate = _.get(_.find(opts, o => o.label === 'Beginning Date'), 'value', '');
        const endingDate = _.get(_.find(opts, o => o.label === 'Ending Date'), 'value', '');
        const includeLabor = _.get(_.find(opts, o => o.label === 'Include Labor'), 'value', 'Y');
        const reportOnCounter = _.get(_.find(opts, o => o.label === 'Report On (Counter)'), 'value', 'P');
        const reportOnSalesman = _.get(_.find(opts, o => o.label === 'Report On (Salesman)'), 'value', 'P');
        const reportOnInternal = _.get(_.find(opts, o => o.label === 'Include Internal'), 'value', 'N');
        const reportOnWarranty = _.get(_.find(opts, o => o.label === 'Include Warranty'), 'value', 'N');
        const reportMode = 'day';

        const lookupKeyOverride = [
          params.column.colId,
          params.node.data.cusno,
          includeLabor,
          reportOnCounter,
          reportOnSalesman,
          beginningDate,
          endingDate,
          reportOnInternal,
          reportOnWarranty,
          reportMode,
        ].join('!!:!!');

        fetchDrilldown('invoiceHistory', lookupKeyOverride).then(data => {
          drilldownModalContent.innerHTML = data;
          drilldownModalContent.style.width = '650px';
          drilldownModal.open();
        });
      },
    });
  }

  if (_.includes(['PPO15'], reportConfig.reportName) && /^\d{1,2}-\d{4}$/.test(params.column.colId)) {
    contextMenu.push({
      icon: '<i class="fas fa-th-list"></i>',
      name: 'Purchase Order History',
      action() {
        const opts = reportModule.getReportOptions();
        const beginningDate = _.get(_.find(opts, o => o.label === 'Beginning Date'), 'value', '');
        const endingDate = _.get(_.find(opts, o => o.label === 'Ending Date'), 'value', '');
        const reportTypes = _.get(_.find(opts, o => o.label === 'Types'), 'value', 'A');
        const reportOn = _.get(_.find(opts, o => o.label === 'Report On'), 'value', 'P');
        const lookupKeyOverride = [
          params.column.colId,
          params.node.data.Vendor,
          reportTypes,
          reportOn,
          'popup',
        ].join('!!:!!');
        modal.open('poDetailList', {
          branchId: branchID,
          lookupKey: lookupKeyOverride,
          title: 'Purchase Orders',
        });
      },
    });
  }

  // custom context menu for PSH1A/PSH1B dynamic month-year totals columns
  if (_.includes(['PSH1A', 'PSH1B'], reportConfig.reportName) && /^\d{4}-\d{1,2}$/.test(params.column.colId)) {
    contextMenu.push({
      icon: '<i class="fas fa-th-list"></i>',
      name: 'Invoice History',
      action() {
        const opts = reportModule.getReportOptions();
        const beginningDate = _.get(_.find(opts, o => o.label === 'Beginning Date'), 'value', '');
        const endingDate = _.get(_.find(opts, o => o.label === 'Ending Date'), 'value', '');
        const reportOnCounter = _.get(_.find(opts, o => o.label === 'Report On'), 'value', 'P');
        const lookupKeyOverride = [
          params.column.colId,
          params.node.data.stock_class,
          reportOnCounter,
          beginningDate,
          endingDate,
        ].join('!!:!!');

        fetchDrilldown('invoiceHistoryFromStockClass', lookupKeyOverride).then(data => {
          drilldownModalContent.innerHTML = data;
          drilldownModalContent.style.width = '650px';
          drilldownModal.open();
        });
      },
    });
  }

  if (contextMenu.length > 0) {
    contextMenu.push('separator');
  }

  if (rangecharts) {
    contextMenu.push('chartRange', 'separator');
  }

  contextMenu.push('copy', 'copyWithHeaders');

  // If this report run includes a branch selection other than the user's currently active
  // branch, remove RO attachment modals pending completion of blaze/attachments-multibranch.
  if (reportModule.isAlternateBranchesRequested()) {
    contextMenu = contextMenu.filter(
      item => !_.includes([modals['RO Attachments'], modals['RO Attachments Read Only']], item)
    );
  }

  return contextMenu;
}

function printCheck() {
  const header = document.getElementById('report-modal-header').innerHTML;
  const checkDiv = document.getElementById('report-modal-check');

  if (checkDiv === null) return; // is there data to print?

  const newWin = window.open('', 'Print', 'width=850,height=750'); // 8x11
  newWin.document.open();
  newWin.document.write(`
  <html>
  <style type="text/css" media="print">
    @page { size: landscape; }
  </style>
  <body onload="window.print()">
    <h2>${header}</h2>
    <div style="border:1px solid #000;margin-top:50px;padding:25px;">
    ${checkDiv.innerHTML}
    </div>
  </body>
  </html>
  `);
  newWin.document.close();
}

function printRawText() {
  const rawText = document.getElementById('report-rawtext-data');

  if (rawText === null) return; // is there data to print?

  const newWin = window.open('', 'Print', 'width=850,height=750'); // 8x11
  newWin.document.open();
  newWin.document.write(`<html><body onload="window.print()"><pre>${rawText.innerHTML}</pre></body></html>`);
  newWin.document.close();
}

function reverseNodeOrder(container) {
  const n = document.getElementById(container);
  if (!n) return;
  const kids = n.childNodes; // Get the list of children
  const numkids = kids.length; // Figure out how many stories there are

  // Loop through them backwards
  for (let i = numkids - 1; i >= 0; i--) {
    const c = n.removeChild(kids[i]); // Remove a child
    n.appendChild(c); // Put it back at its new position
  }
}

function saveCollection() {
  const customerNo = document.getElementById('customerNo');
  const collectionContact = document.getElementById('collectionContact');
  const collectionContactDate = document.getElementById('collectionContactDate');
  const collectionContactTime = document.getElementById('collectionContactTime');
  const collectionPromiseDate = document.getElementById('collectionPromiseDate');
  const collectionPromiseAmount = document.getElementById('collectionPromiseAmount');
  const collectionNote = document.getElementById('collectionNote');

  // validate the form
  let valid = true;

  // ck wrote these regex - hopefully not buggy
  // MM-DD-YYYY with/without leading 0s on m/d, limits months/days to reasonable calendar inputs (M:1-12, D:1-31)
  const validDate = /^(0*[1-9]|1[0-2])-(0*[1-9]|[1-2][0-9]|3[0-1])-\d{4}$/;
  // HH:MM 24hr format, leading 0 on H ok
  const validTime = /^(0*[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
  // 25325.22 commas not supported (D3/QM doesn't support), decimal and pennies optional
  const validCurrency = /^\d+$|^\d+\.\d\d$/;

  if (collectionContact.value.trim() === '') {
    collectionContact.style.border = '1px solid red';
    valid = false;
  }
  if (collectionContactDate.value.trim() === '' || !collectionContactDate.value.match(validDate)) {
    collectionContactDate.style.border = '1px solid red';
    valid = false;
  }
  if (collectionContactTime.value.trim() === '' || !collectionContactTime.value.match(validTime)) {
    collectionContactTime.style.border = '1px solid red';
    valid = false;
  }
  if (collectionPromiseAmount.value.trim() === '' || !collectionPromiseAmount.value.match(validCurrency)) {
    collectionPromiseAmount.style.border = '1px solid red';
    valid = false;
  }
  // ok to be empty
  if (collectionPromiseDate.value.trim() !== '' && !collectionPromiseDate.value.match(validDate)) {
    collectionPromiseDate.style.border = '1px solid red';
    valid = false;
  }
  if (collectionNote.value.trim() === '') {
    collectionNote.style.border = '1px solid red';
    valid = false;
  }

  if (!valid) {
    toastr.error('Error!', 'Please correct red inputs.');
    return; // do not submit form if invalid
  }

  const postBody = {
    branchID, // null when non-mb run
    customerNo: customerNo.value,
    collectionContact: collectionContact.value,
    collectionContactDate: collectionContactDate.value,
    collectionContactTime: collectionContactTime.value,
    collectionPromiseDate: collectionPromiseDate.value,
    collectionPromiseAmount: collectionPromiseAmount.value,
    collectionNote: collectionNote.value,
  };

  Api.post('report/addcollectionnote', postBody)
    .then(res => {
      if (res.data !== 200) {
        throw new Error();
      }

      toastr.success('Success!', 'Collection note added.');

      // update collection modal
      fetchDrilldown('customerCollections').then(data => {
        drilldownModalContent.innerHTML = data;

        // rebind pickaday controls
        // cleanup previous pikaday resources
        pikaDayObjs.forEach(pikaDayObj => {
          pikaDayObj.destroy();
        });
        pikaDayObjs = [];
        // rebind pikaDay after refreshing modal
        pikaDayObjs.push(
          new Pikaday({
            field: document.getElementById('collectionContactDate'),
            format: 'MM-DD-YYYY',
            trigger: document.getElementById('collectionContactDateCal'),
          })
        );
        pikaDayObjs.push(
          new Pikaday({
            field: document.getElementById('collectionPromiseDate'),
            format: 'MM-DD-YYYY',
            trigger: document.getElementById('collectionPromiseDateCal'),
          })
        );
      });
    })
    .catch(() => {
      toastr.error('Error!', 'Unable to add collection note.  Try again later.');
    });
}

// Adds/Updates a Cause/Correction to the technician story of a RO
function saveStory() {
  const causeInput = document.getElementById('causeInput');
  const correctionInput = document.getElementById('correctionInput');
  const technicianSelect = document.getElementById('technicianSelect');
  const operationSelect = document.getElementById('operationSelect');
  const roNo = document.getElementById('rono').value;

  // need at least a cause or a correction
  if (causeInput.value.trim() === '' && correctionInput.value.trim() === '') {
    toastr.error('Error!', 'You must specify a cause or correction.');
    return;
  }

  if (technicianSelect.value === 'select') {
    toastr.error('Error!', 'You must select a technician name.');
    return;
  }

  if (operationSelect.value === 'select') {
    toastr.error('Error!', 'You must select an operation number.');
    return;
  }

  /* inject D3/QM newline markers (^^) where appropriate (70 chars/line max)
     1. normalize all browser textarea newline schemes to \n
     2. split lines longer than 70chars on ^^ delimeter
     3. join all lines (possibly split-edited) back on ^^
  */
  let cause = causeInput.value.trim();
  let correction = correctionInput.value.trim();

  // 1. Normalize user supplied newlines generated when user presses the enter key
  //   (diff browsers use different schemes - do the regex IN ORDER)
  cause = cause.replace(/\r\n/g, '\n');
  cause = cause.replace(/\r/g, '\n');
  correction = correction.replace(/\r\n/g, '\n');
  correction = correction.replace(/\r/g, '\n');

  // 2. Textareas wrap on screen, but do NOT interject newlines into the actual value,
  //    so we'll do it ourselves with a wordwrap aglorithm that doesn't break words.
  const wordwrapOpts = {
    indent: '',
    newline: '^^',
    trim: true,
    width: 70,
  };

  cause = cause.split('\n'); // splits on any manually entered lines
  for (let i = 0; i < cause.length; i++) {
    if (cause[i].length > 70) {
      cause[i] = wordwrap(cause[i], wordwrapOpts);
    }
  }

  correction = correction.split('\n'); // splits on manually entered lines
  for (let i = 0; i < correction.length; i++) {
    if (correction[i].length > 70) {
      correction[i] = wordwrap(correction[i], wordwrapOpts);
    }
  }

  // 3. Join it all back together
  cause = cause.join('^^');
  correction = correction.join('^^');

  const postBody = {
    branchID, // null when non-mb run
    roNo,
    op: operationSelect.value,
    techID: technicianSelect.value,
    cause,
    correction,
  };

  Api.post('report/addstory', postBody)
    .then(res => {
      if (res.data !== 200) {
        throw new Error();
      }

      // refresh technician story
      toastr.success('Success!', 'Technician story updated.');
      fetchDrilldown('addViewTechnicianStory').then(data => {
        drilldownModalContent.innerHTML = data;
      });
    })
    .catch(() => {
      toastr.error('Error!', 'Unable to add story.  Try again later.');
    });
}

function setCollectionTimeToNow(event) {
  const collectionContactDate = document.getElementById('collectionContactDate');
  const collectionContactTime = document.getElementById('collectionContactTime');

  collectionContactDate.value = moment().format('MM-DD-YYYY');
  collectionContactTime.value = moment().format('HH:mm');
  collectionContactDate.style.border = '';
  collectionContactTime.style.border = '';
  event.preventDefault();
}

// called via ReportController to give this module a way to refresh the widget data (WEBRO2)
function setGridRefresh(refreshFunc) {
  gridRefresh = refreshFunc;
}

// called via ReportController so this module has access to the report config
function setReportConfig(config) {
  reportConfig = config;
}

// note subModals only support a small subset of the modals above and are, by neccessity
// redefined here to use the submodal infrastructure
function subModal(drilldown, key) {
  switch (drilldown) {
    case 'documentDisplay':
      lookupKey = key;
      fetchDrilldown('documentDisplay', null, true).then(data => {
        drilldownSubModalContent.innerHTML = data;
        drilldownSubModalContent.style.width = '1300px';
        drilldownSubModal.open();
      });
      break;
    case 'customerDetail':
      lookupKey = key;
      fetchDrilldown('customerDetail', null, true).then(data => {
        drilldownSubModalContent.innerHTML = data;
        drilldownSubModalContent.style.width = '800px';
        drilldownSubModal.open();
      });
      break;
    case 'detail':
      lookupKey = key;
      fetchDrilldown('detail', null, true).then(data => {
        drilldownSubModalContent.innerHTML = data;
        drilldownSubModalContent.style.width = '875px';

        if (_.includes(data, 'partDetailAvailable')) {
          const tmp = JSON.parse(document.getElementById('detailModalGraphData').value);
          const dataProvider = [];
          _.each(tmp, (v) => {
            dataProvider.push({ date: v[0], value: v[1] });
          });

          const chartConfigJSON = {
            theme: 'material',
            type: 'serial',
            categoryField: 'date',
            graphs: [
              {
                bullet: 'round',
                valueField: 'value',
                balloonText:
                  '<div style="margin:5px; font-size:19px;"><span style="font-size:13px;">[[category]]</span><br>[[value]]</div>',
              },
            ],
            valueAxis: { labelsEnabled: true },
            export: { enabled: true },
            dataProvider,
          };

          // webpack compatibility - see require at head of module
          chartConfigJSON.pathToImages = '/img/amcharts/';

          // AmCharts cleanup occurs in rModal afterClose() at the top of this module
          chartObjs.push(AmCharts.makeChart('detailModalGraph', chartConfigJSON));
        }

        drilldownSubModal.open();
      });
      break;
    case 'glDetail':
      lookupKey = key;
      fetchDrilldown('glDetail', null, true).then(data => {
        drilldownSubModalContent.innerHTML = data;
        drilldownSubModalContent.style.width = '800px';
        drilldownSubModal.open();
      });
      break;
    case 'stockclassDetail':
      lookupKey = key;
      fetchDrilldown('stockclassDetail', null, true).then(data => {
        drilldownSubModalContent.innerHTML = data;
        drilldownSubModalContent.style.width = '800px';
        drilldownSubModal.open();
      });
      break;
    case 'stockclassParts':
      lookupKey = key;
      fetchDrilldown('stockclassParts', null, true).then(data => {
        drilldownSubModalContent.innerHTML = data;
        drilldownSubModalContent.style.width = '800px';
        drilldownSubModal.open();
      });
      break;
    case 'viewAndPrintInvoice':
      lookupKey = key;
      fetchDrilldown('viewAndPrintInvoice', null, true).then(data => {
        drilldownSubModalContent.innerHTML = data;
        drilldownSubModalContent.style.width = '800px';
        drilldownSubModal.open();
      });
      break;
    default:
      break;
  }
}

// global exports namespaced to drilldownsModule
// keep this as minimal as possible
// drilldown modals call these functions evented from user interactions
window.drilldownsModule = {
  addCollectionNote,
  addRONote,
  addScheduleNote,
  addStory,
  cancelCollectionNote,
  cancelStory,
  clearCollection,
  clearCollectionError,
  clearStory,
  close,
  printCheck,
  printRawText,
  reverseNodeOrder,
  saveCollection,
  saveStory,
  setCollectionTimeToNow,
  subModal,
};

// es6 exports used by ReportController
export default {
  init,
  destroy,
  getContextMenuItems,
  setGridRefresh,
  setReportConfig,
};
